/**
 * RBAC helpers.
 *
 * Central definition of what each role can do. Components/guards call `can()`
 * rather than checking role strings inline, so permission changes live in one
 * place and are easy to audit.
 */
export const PERMISSIONS = Object.freeze({
  VIEW_RECOMMENDATIONS: 'recommendations:view',
  REGENERATE_COST_ADVICE: 'recommendations:regenerate',
  EXPORT_REPORT: 'recommendations:export',
  MANAGE_PORTFOLIOS: 'portfolios:manage',
  ADMIN_VIEW_ALL: 'admin:viewAll',
  /** The Analytics v2 page. */
  VIEW_ANALYTICS: 'analytics:view',
  /** The sidebar's "Org Portfolios" scope switch — see everyone's, not just mine. */
  VIEW_ORG_PORTFOLIOS: 'portfolios:viewOrg',
});

/*
 * App roles, not server roles. features/auth/roles.js translates the server's
 * `role_name` into these, and that translation is variant-aware — which is the
 * only reason this table does not need to be.
 *
 * `analytics` is separate from `admin` because the two products disagree about
 * who gets Analytics: CCA grants it to super_admin AND super_admin_analytics,
 * EIA only to super_admin_analytics. Folding both server roles into one app role
 * would lose that distinction and hand EIA admins a page the server denies them.
 */
const ROLE_PERMISSIONS = Object.freeze({
  user: [
    PERMISSIONS.VIEW_RECOMMENDATIONS,
    PERMISSIONS.REGENERATE_COST_ADVICE,
    PERMISSIONS.EXPORT_REPORT,
    PERMISSIONS.MANAGE_PORTFOLIOS,
  ],
  admin: [
    PERMISSIONS.VIEW_RECOMMENDATIONS,
    PERMISSIONS.REGENERATE_COST_ADVICE,
    PERMISSIONS.EXPORT_REPORT,
    PERMISSIONS.MANAGE_PORTFOLIOS,
    PERMISSIONS.ADMIN_VIEW_ALL,
  ],
  analytics: [PERMISSIONS.VIEW_ANALYTICS],
  /** Legacy's `admin` role, which gates only the Org Portfolios switch. */
  org_admin: [PERMISSIONS.VIEW_ORG_PORTFOLIOS],
});

/**
 * @param {string[]} roles - roles held by the current user
 * @param {string} permission - a PERMISSIONS.* value
 */
export function can(roles, permission) {
  if (!Array.isArray(roles)) return false;
  return roles.some((role) => ROLE_PERMISSIONS[role]?.includes(permission));
}

export function hasAnyRole(roles, allowed) {
  if (!Array.isArray(roles)) return false;
  return roles.some((r) => allowed.includes(r));
}
